import moment from 'moment';
import fp from 'lodash/fp';

import * as appConstants from '../../common/constants';

const serverFiltersMapping = filters => filters.filter(
    filter => ['runDate', 'csa', 'counterparty'].indexOf(filter.definition.id) >= 0
);

const runDateFilter = (filter, reducedFilters) => (filter.value.today
    ? fp.set(filter.definition.id, moment().format(appConstants.dateFormat), reducedFilters)
    : fp.set(filter.definition.id, moment(filter.value.from).format(appConstants.dateFormat), reducedFilters));

const setEndDate = filter => reducedFilters => (
    filter.value.to ? fp.set('runEndDate', moment(filter.value.to).format(appConstants.dateFormat), reducedFilters) : reducedFilters
);

const setStartDate = filter => reducedFilters => (
    filter.value.from ? fp.set('runStartDate', moment(filter.value.from).format(appConstants.dateFormat), reducedFilters) : reducedFilters
);

const runDateFilterRange = (filter, reducedFilters) => (filter.value && filter.value.today
    ? {
        runStartDate: moment().format(appConstants.dateFormat),
        runEndDate: moment().format(appConstants.dateFormat),
        ...reducedFilters
    } : fp.flow(setStartDate(filter), setEndDate(filter))(reducedFilters));

export const adaptDrilldownFilters = (filters = []) => fp.reduce((reducedFilters, filter) => {
    if (filter.value === undefined || filter.value === null) {
        return reducedFilters;
    }
    switch (filter.definition.id) {
        case 'runDate':
            return runDateFilter(filter, reducedFilters);
        case 'csa':
            return fp.set(`${filter.definition.id}Id`, filter.value[0], reducedFilters);
        default:
            break;
    }
    return reducedFilters;
}, {}, serverFiltersMapping(filters));

export const adaptImpactFilters = (filters = []) => fp.reduce((reducedFilters, filter) => {
    if (filter.value === undefined || filter.value === null) {
        return reducedFilters;
    }
    switch (filter.definition.id) {
        case 'runDate':
            return runDateFilter(filter, reducedFilters);
        case 'csa':
            return fp.set(`${filter.definition.id}`, filter.value[0], reducedFilters);
        default:
            break;
    }
    return reducedFilters;
}, {}, serverFiltersMapping(filters));

export const adaptSummaryFilters = (filters = []) => fp.reduce((reducedFilters, filter) => {
    if (filter.value === undefined || filter.value === null) {
        return reducedFilters;
    }
    switch (filter.definition.id) {
        case 'runDate':
            return runDateFilterRange(filter, reducedFilters);
        case 'csa':
            return fp.set('csa', filter.value, reducedFilters);
        case 'counterparty':
            return fp.set('counterparty', filter.value, reducedFilters);
        default:
            break;
    }
    return reducedFilters;
}, {}, serverFiltersMapping(filters));

export const adaptImMonitoringFilters = (filters = []) => fp.reduce(
    (reducedFilters, filter) => {
        if (!filter.value) { return reducedFilters; }

        switch (filter.definition.id) {
            case 'counterparties':
                return fp.set('counterparty', filter.value)(reducedFilters);
            case 'date':
                return fp.flow(
                    fp.set('runStartDate', moment(filter.value.from, 'YYYYMMDD').format(appConstants.dateFormat)),
                    fp.set('runEndDate', moment(filter.value.to, 'YYYYMMDD').format(appConstants.dateFormat))
                )(reducedFilters);
            default:
                return reducedFilters;
        }
    },
    {}
)(filters);

export const restructureListByType = csas => csas.reduce(
    (acc, cur) => {
        const possibleParent = acc.findIndex(a => a.title === cur.type);
        if (possibleParent === -1) {
            return [
                ...acc,
                {
                    title: cur.type,
                    children: [
                        cur
                    ]
                }
            ];
        }
        return fp.set(`[${possibleParent}].children`, [...acc[possibleParent].children, cur])(acc);
    },
    []
);

export const createCounterpartyList = csaList => csaList.reduce(
    (acc, cur) => {
        const index = fp.findIndex(c => c.title === cur.counterparty)(acc);
        if (index === -1) {
            return [
                ...acc,
                {
                    title: cur.counterparty,
                    value: cur.counterparty,
                    type: cur.type
                }
            ];
        }
        return acc;
    },
    []
);
